import { DataTable } from '@peritasai/ui-components';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import type { FacilityEntity } from '@peritasai/repository';
import { useTranslation } from 'react-i18next';
import { renderTableCell } from '../shared/renderTableCell';

const to12Hr = (time: string | null | undefined): string | null => {
  if (!time || !time.trim()) return null;
  const match = time.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const h24 = parseInt(match[1], 10);
  const min = match[2];
  const period = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 || 12;
  return `${String(h12).padStart(2, '0')}:${min} ${period}`;
};

const getColumns = (
  t: (key: string) => string
): GridColDef<FacilityEntity>[] => [
  {
    field: 'name',
    headerName: t('pages.entityManagement.facilitiesTable.columnName'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value, true),
  },
  {
    field: 'address',
    headerName: t('pages.entityManagement.facilitiesTable.columnAddress'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'startTime',
    headerName: t('pages.entityManagement.facilitiesTable.columnOperatingTimes'),
    flex: 1,
    renderCell: (params) => {
      const start = to12Hr(params.row.startTime);
      const end = to12Hr(params.row.endTime);
      const value = start && end ? `${start} - ${end}` : null;
      return renderTableCell(value);
    },
  },
  {
    field: 'timeZone',
    headerName: t('pages.entityManagement.facilitiesTable.columnTimeZone'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'contactInfo',
    headerName: t('pages.entityManagement.facilitiesTable.columnContactInfo'),
    flex: 1,
    headerClassName: 'last-column-header',
    renderCell: (params) => renderTableCell(params.value),
  },
];

interface FacilitiesTableProps {
  rows: FacilityEntity[];
  rowCount: number;
  isLoading: boolean;
  hasSearch: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: FacilityEntity) => void;
}

export const FacilitiesTable = ({
  rows,
  rowCount,
  isLoading,
  hasSearch,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
}: FacilitiesTableProps) => {
  const { t } = useTranslation(['periverse']);
  const columns = getColumns(t);

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={rowCount}
      isLoading={isLoading}
      hasSearch={hasSearch}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={onRowClick}
      searchEmptyTitle={t('pages.entityManagement.table.searchEmptyTitle')}
      searchEmptyMessage={t('pages.entityManagement.table.searchEmptyMessage')}
      emptyMessage={t('pages.entityManagement.table.noRows', {
        entityName: t('pages.entityManagement.items.facilities'),
      })}
      skeletonColumnCount={5}
    />
  );
};
